'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { Site } from '@/app/types/global';
import { cachedFetchSites } from '@/app/utils/utils';
import { settingsLandingPath } from '@/app/lib/siteSettings';
import Loading from '@/app/components/loading';
import '@/app/globals.css';

// Top-level Settings entry point. Site configuration is per-site, so this
// page just gets the owner to the right site's settings hub: one site goes
// straight there, several show a picker, none points at onboarding. (This
// replaced the old SiteManager form here, which duplicated /site/{id}/edit.)
const Page = () => {
  const router = useRouter();
  const [sites, setSites] = useState<Site[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await cachedFetchSites();
        const data = response.ok ? ((await response.clone().json()) as Site[]) : [];
        if (!cancelled) setSites(data);
      } catch {
        if (!cancelled) setSites([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!sites) return;
    const landing = settingsLandingPath(sites);
    if (landing) router.replace(landing);
  }, [sites, router]);

  if (!sites || settingsLandingPath(sites)) {
    return <Loading title="Loading Settings..." />;
  }

  return (
    <div className="container mx-auto max-w-3xl p-4 py-8">
      <h1 className="text-2xl font-bold text-foreground border-b-2 border-blue-400 pb-2 inline-block">
        Settings
      </h1>
      {sites.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-4">
          Settings are per site, and there is no site on your account yet. Once
          your system is connected through{' '}
          <Link href="/configure" className="text-blue-600 dark:text-blue-400 hover:underline">
            Configure
          </Link>
          , its settings will appear here.
        </p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mt-3 mb-6">
            Choose the site you want to configure.
          </p>
          <div className="space-y-3">
            {sites.map((site) => (
              <Link
                key={site.id}
                href={`/site/${site.id}/settings`}
                className="group flex items-center justify-between bg-card border border-border rounded-lg p-4 hover:border-blue-400 transition-colors"
              >
                <span className="font-semibold text-card-foreground">
                  {site.display_name || site.name || `Site ${site.id}`}
                </span>
                <ChevronRight className="w-5 h-5 shrink-0 ml-4 text-muted-foreground group-hover:text-blue-500 transition-colors" />
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Page;
